import { RouteUrl } from "@/configs/router/routes";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ExportToExcelButton } from "@/components/general/common/ExportToExcelButton";
import { exportPlayerComparisonToExcel } from "@/api/excelExport";
import { useSelectedTeam } from "@/context/teamContext";
import type { StatCategory as TeamStatCategory } from "./TournamentRankingTable";
import type { StatCategory as PlayerStatCategory } from "@/components/general/Scouting/ScoutingPlayersTable";
import type { StatCategory as GoalkeeperStatCategory } from "@/components/general/Scouting/ScoutingGoalkeepersTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import MaterialIcon from "@/components/general/common/MaterialIcon";

export type StatCategory = TeamStatCategory | PlayerStatCategory | GoalkeeperStatCategory;

const navItems = [
    { to: RouteUrl.TournamentOverview, label: "Overview" },
    { to: RouteUrl.TournamentCompare, label: "Compare" },
    { to: RouteUrl.TournamentRanking, label: "Team Ranking" },
    { to: RouteUrl.TournamentLines, label: "Lines" },
];

interface TournamentAnalysisNavigationProps {
    viewMode?: "average" | "total";
    onViewModeChange?: (mode: "average" | "total") => void;
    selectedTournamentId?: number;
    selectedPlayers?: Array<{ playerId: number; firstName: string; lastName: string }>;
    statCategory?: StatCategory;
    onStatCategoryChange?: (category: StatCategory) => void;
}

const TournamentAnalysisNavigation = ({
    viewMode = "average",
    onViewModeChange,
    selectedTournamentId,
    selectedPlayers = [],
    statCategory = "overview",
    onStatCategoryChange,
}: TournamentAnalysisNavigationProps) => {
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const navigate = useNavigate();
    const { teamId } = useSelectedTeam();

    const showViewModeSwitch = pathname === RouteUrl.TournamentCompare;
    const showCompareExportButton = pathname === RouteUrl.TournamentCompare;
    const showStatCategorySwitch =
        pathname === RouteUrl.TournamentRanking ||
        pathname === RouteUrl.TournamentPlayers ||
        pathname === RouteUrl.TournamentMatches;

    const getStatCategories = () => {
        if (pathname === RouteUrl.TournamentRanking) {
            return [
                { id: "overview", label: "Overview" },
                { id: "shooting", label: "Shooting" },
                { id: "passing", label: "Passing" },
                { id: "puck", label: "Puck Control" },
                { id: "faceoffs", label: "Faceoffs" },
                { id: "advanced", label: "Advanced" },
                { id: "special", label: "Special Teams" },
            ];
        } else if (pathname === RouteUrl.TournamentPlayers) {
            return [
                { id: "overview", label: "Overview" },
                { id: "skating", label: "Skating" },
                { id: "puck", label: "Puck Control" },
                { id: "actions", label: "Actions" },
                { id: "passing", label: "Passing" },
                { id: "advanced", label: "Advanced" },
            ];
        } else if (pathname === RouteUrl.TournamentMatches) {
            return [
                { id: "overview", label: "Overview" },
                { id: "performance", label: "Performance" },
                { id: "passing", label: "Passing" },
                { id: "screens", label: "Screens" },
            ];
        }
        return [{ id: "overview", label: "Overview" }];
    };

    const statCategories = getStatCategories();

    return (
        <div className="flex flex-wrap gap-3 mb-1 items-center justify-between">
            <nav
                id="onboarding-tournament-nav"
                className={`rounded-md p-1 bg-surface-container-low basis-full ${showViewModeSwitch || showStatCategorySwitch ? "md:basis-0 md:flex-1" : ""}`}
            >
                <div className="grid grid-cols-2 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                    {navItems.map((item) => {
                        const isActive = pathname === item.to;
                        return (
                            <button
                                key={item.to}
                                type="button"
                                className={`nav-tab ${isActive ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => {
                                    if (!isActive) {
                                        navigate({ to: item.to });
                                    }
                                }}
                            >
                                {item.label}
                            </button>
                        );
                    })}
                </div>
            </nav>
            {showViewModeSwitch && onViewModeChange && (
                <div className="flex flex-col gap-2 basis-full md:flex-row md:items-center md:basis-0 md:flex-1">
                    <nav
                        id="onboarding-tournament-view-mode"
                        className="rounded-md p-1 bg-surface-container-low flex-1"
                    >
                        <div className="grid grid-cols-2 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            <button
                                type="button"
                                className={`nav-tab ${viewMode === "average" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => onViewModeChange("average")}
                            >
                                Average
                            </button>
                            <button
                                type="button"
                                className={`nav-tab ${viewMode === "total" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => onViewModeChange("total")}
                            >
                                Total
                            </button>
                        </div>
                    </nav>
                    {showCompareExportButton && (
                        <nav
                            id="onboarding-tournament-compare-export"
                            className="rounded-md p-1 bg-surface-container-low shrink-0"
                        >
                            <ExportToExcelButton
                                mutationFn={() => {
                                    if (!selectedPlayers[0]?.playerId || !selectedPlayers[1]?.playerId) {
                                        throw new Error("Both players must be selected for comparison export");
                                    }
                                    return exportPlayerComparisonToExcel(
                                        selectedTournamentId!,
                                        selectedPlayers[0].playerId,
                                        selectedPlayers[1].playerId,
                                        teamId
                                    );
                                }}
                                label="Export Comparison to Excel"
                                successMessage={`Player comparison Excel file for ${selectedPlayers[0]?.firstName} ${selectedPlayers[0]?.lastName} vs ${selectedPlayers[1]?.firstName} ${selectedPlayers[1]?.lastName} downloaded successfully!`}
                                disabled={!selectedPlayers[0]?.playerId || !selectedPlayers[1]?.playerId}
                            />
                        </nav>
                    )}
                </div>
            )}
            {showStatCategorySwitch && onStatCategoryChange && (
                <div id="onboarding-tournament-stat-category" className="flex items-center w-full sm:w-auto shrink-0">
                    <Select value={statCategory} onValueChange={(v) => onStatCategoryChange(v as StatCategory)}>
                        <SelectTrigger className="w-full sm:w-fit bg-surface-container-highest border-0">
                            <div className="flex items-center gap-2">
                                <MaterialIcon name="filter_list" className="text-primary text-lg" />
                                <SelectValue />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            {statCategories.map((category) => (
                                <SelectItem key={category.id} value={category.id}>
                                    {category.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
};

export default TournamentAnalysisNavigation;
